/** The composed-module ABI. A module is ONE rule a designer would name,
 * configured by data, owning only its declared state, talking to the rest of
 * the game through the `ModuleCtx` capability (no clock, no Math.random, no
 * IO, no host handles). */
import type { JsonObject, JsonValue } from '../json.js';
import type { GuestFaultReasonV1 } from '../guestBudget.js';
import type { ConfigSchema } from '../schema.js';
import type { AnimStateName } from '../animStates.js';
import type { EntityV1, PlayerInputV1, SeatInputV1, SignalV1, WorldV1, EntityStatusV1 } from './world.js';

/** Fixed stage order. Within a stage, module instances run in compiled order
 * (globals in spec order, then each archetype's modules in spec order) and
 * entity instances in ascending id order. */
export const COMPOSE_STAGES = ['input', 'think', 'move', 'collide', 'interact', 'rules', 'flow', 'present'] as const;
export type ComposeStage = typeof COMPOSE_STAGES[number];

/** Module role. Exactly one `movement` module per archetype owns position
 * integration (enforced at compile time). */
export type ModuleRole = 'movement' | 'behaviour' | 'rule' | 'world' | 'flow' | 'presentation';

export type HudKind = 'counter' | 'pips' | 'meter' | 'timer' | 'text';
export interface HudFieldDeclV1 { id: string; label: string; kind: HudKind }
export type HudScalar = number | string;
/** Per-slot fields return one value per player seat. */
export type HudFieldValue = HudScalar | number[];

export interface SpawnInitV1 {
  x: number;
  y: number;
  /** Elevation; absent ⇒ the archetype's `elevation`. */
  z?: number;
  facing?: 1 | -1;
  status?: EntityStatusV1;
  slot?: number;
  /** Opaque per-spawn parameters each module's `init` may read. */
  params?: JsonObject;
  /** Presentation overlay seeds (a placed tool's name and item colour). */
  tint?: number;
  label?: string;
}

/** A point the generic solver may steer a player towards. Modules publish
 * these as affordances; the solver never knows game-specific rules. */
export interface SolverTargetV1 {
  x: number;
  y: number;
  /** Higher = more attractive. */
  weight: number;
  /** Diagnostic label (`kick`, `flip-from-below`, `collect`). */
  label: string;
  /** When true, the solver should press `a` (the primary verb) near it. */
  press?: boolean;
}

/** A one-way traversal a module opens for a player (ladder, slide,
 * transporter): from a standing point to another, at a path cost in world
 * units. The solver's navigator adds it to the surface graph; a two-way
 * traversal publishes both directions. */
export interface NavLinkV1 {
  from: { x: number; y: number };
  to: { x: number; y: number };
  cost: number;
  /** The vertical input that starts it, pressed at `from` (up −1, down 1). */
  dy: -1 | 1;
  /** True when the body travels along the from→to segment (a ladder, a
   * chute); false for a jump between its ends (a transporter). */
  along: boolean;
  /** Diagnostic label (`ladder`, `slide`, `teleport`). */
  label: string;
}

/** A solid ground profile a world module publishes (a heightfield's rock
 * line): a world-space polyline `[x0, y0, x1, y1, …]`, left to right, that
 * the platform draws filled down to the bottom of the world in palette colour
 * `fill`, with its top edge stroked in palette colour `edge`. Presentation
 * only — collision against it is the publishing module's own query. */
export interface ComposedGroundV1 {
  points: number[];
  fill: number;
  edge: number;
}

/** Shell effect for an event (the shell's closed vfx vocabulary). */
export type ComposedVfxV1 =
  | { effect: 'burst'; palette?: number | 'slot'; count?: number }
  | { effect: 'popup'; text: string; palette?: number | 'slot' }
  | { effect: 'shake' }
  | { effect: 'flash'; palette?: number | 'slot' };

/** How an event presents by default: a platform cue name (the creations cue
 * vocabulary, or 'none' for a deliberately silent moment) and an optional
 * visual effect. Positional effects read `data.x`/`data.y`. */
export interface EventPresentationV1 { cue: string; vfx?: ComposedVfxV1 }

export interface ModuleCtx<C> {
  readonly world: WorldV1;
  readonly config: C;
  /** Module instance key (global) or module id (entity). */
  readonly key: string;
  readonly dt: number;
  /** Mutable module-owned global state (one object per instance key). */
  readonly state: JsonObject;
  random(): number;
  randomInt(exclusiveMax: number): number;
  /** Entities in ascending id order. `status: 'any'` includes hidden ones. */
  entities(filter?: { tag?: string; archetype?: string; status?: 'any' | 'visible' | 'active' }): EntityV1[];
  entity(id: string | null): EntityV1 | undefined;
  /** This module's component data on `entity` (created by init). */
  data(entity: EntityV1): JsonObject;
  /** Another module's component data, when the entity carries it. */
  peer(entity: EntityV1, moduleId: string): JsonObject | undefined;
  /** Another module's config on the entity's archetype, when present. */
  peerConfig(entity: EntityV1, moduleId: string): JsonObject | undefined;
  spawn(archetype: string, init: SpawnInitV1): EntityV1;
  despawn(entity: EntityV1, reason: string): void;
  emit(kind: string, data?: JsonObject): void;
  signal(kind: string, target: EntityV1 | null, source: EntityV1 | null, data?: JsonObject): void;
  signals(kind: string): SignalV1[];
  /** Propose this tick's animation state. Later stages override earlier ones;
   * a state outside this module's declared `animStates` throws. */
  setAnim(entity: EntityV1, state: AnimStateName, retrigger?: boolean): void;
  react(entity: EntityV1, reaction: string): void;
  addScore(slot: number, points: number): void;
  input(slot: number): SeatInputV1;
  scalar(name: string, fallback: number): number;
  setScalar(name: string, value: number): void;
  /** End the match. A versus ending names the winning seat (null: a draw);
   * solo and co-op endings name none. */
  end(result: 'won' | 'lost', reason: string, winner?: number | null): void;
  /** Archetype names carrying `moduleId`. */
  archetypesWith(moduleId: string): string[];
  /** Entities of the kinds whose archetype carries `tag` that the game will
   * still place this match, or null when it cannot say: some module other
   * than the caller names such a kind in its config and declares no
   * {@link ComposeModuleV1.placementsAhead} schedule (a spawner, a drop). A
   * module places only kinds its config names. */
  placementsAhead(tag: string): number | null;
  hasModule(entity: EntityV1, moduleId: string): boolean;
}

export interface ModuleValidationContextV1 {
  archetypes: Readonly<Record<string, { tags: readonly string[]; modules: readonly string[] }>>;
  globals: readonly string[];
  world: { w: number; h: number };
  /** Number of palette colours (tint indices must stay below it). */
  paletteSize: number;
}

export interface ComposeModuleV1<C = JsonObject> {
  id: string;
  version: number;
  attach: 'entity' | 'global';
  role: ModuleRole;
  summary: string;
  /** Closed config schema (the kit vocabulary). */
  config: ConfigSchema;
  /** Module ids that must also be present somewhere in the spec. */
  requires: readonly string[];
  /** Presentation event kinds this module may emit. */
  events: readonly string[];
  /** Default cue/vfx for every declared event (no event is silently unpresented). */
  presentation: Readonly<Record<string, EventPresentationV1>>;
  /** Animation states this module may put on the entity it is attached to. */
  animStates(config: C): readonly AnimStateName[];
  /** Global modules: states they may put on entities carrying a tag. */
  animStatesForTag?(config: C): ReadonlyArray<{ tag: string; states: readonly AnimStateName[] }>;
  /** Rewrite every archetype name the config references (renames); the
   * reference list itself is derived from this one mapping. */
  mapArchetypeRefs?(config: C, rename: (name: string) => string): C;
  /** Cross-field semantic checks beyond the schema; return error messages. */
  check?(config: C, context: ModuleValidationContextV1, archetype: string | null): string[];
  hud?(config: C): readonly HudFieldDeclV1[];
  hudValues?(ctx: ModuleCtx<C>): Record<string, HudFieldValue>;
  /** Named properties its unit tests prove (documentation + catalog). */
  proofs: readonly string[];
  /** Entity modules: also run on hidden entities (pipes, respawn timers). */
  includeHidden?: boolean;
  /** True for a module through which a body stands on, walks along, climbs
   * or lands on a surface the game draws itself (a platformer body, a ground
   * walker, a ladder, a lander and the terrain it lands on). A game holding
   * one draws its own play surfaces, so its generated background must paint
   * none (`@djgg/creations` `sceneryOnly.ts` reads this). */
  surfaceContact?: true;
  /** A module that places entities on a schedule (an authored layout at
   * match start, a scroll route): how many of `kinds` it will still place
   * this match, or null when its config hands a kind on to another module
   * (a placed entity's params). Without it, a module whose config names a
   * kind may place it at any time. */
  placementsAhead?(ctx: ModuleCtx<C>, kinds: ReadonlySet<string>): number | null;
  initGlobal?(ctx: ModuleCtx<C>): void;
  init?(ctx: ModuleCtx<C>, entity: EntityV1, params: JsonObject): void;
  systems: Partial<Record<ComposeStage, (ctx: ModuleCtx<C>, entity: EntityV1 | null) => void>>;
  solverTargets?(ctx: ModuleCtx<C>, entity: EntityV1 | null, player: EntityV1): SolverTargetV1[];
  /** Work this module instance has seen DONE (claimed cells, collected
   * pickups; `archetype` is the instance's, null for globals), monotonic, in
   * target-weight units: each finished target counts MORE than the weight it
   * was published with, so reaching a target never looks like a lost pull.
   * `player` is the seat's body the bot is judging (null while it has none):
   * work a player PUSHES, a ball it drives toward the goal it attacks, counts
   * for that seat only, and holds the bot to an object a touch sends away
   * (its target's pull alone falls as the ball leaves). */
  solverProgress?(ctx: ModuleCtx<C>, archetype: string | null, player: EntityV1 | null): number;
  /** Traversals this module lets `player` make (player-attached modules are
   * called with the player as `entity`). */
  navLinks?(ctx: ModuleCtx<C>, entity: EntityV1 | null, player: EntityV1): NavLinkV1[];
  /** A global world module that MOVES THE VIEW (an auto-scrolling camera):
   * how far the view has travelled over the ground, in world units on each
   * screen axis. Positions stay view-space; this offset is what the platform
   * scrolls the ground art by. A spec uses one exactly when it declares the
   * `auto-scroll` camera (see `CameraSpecV1`). */
  view?(ctx: ModuleCtx<C>): { x: number; y: number };
  /** A MOVEMENT module whose verbs are not held directions (a thrust-and-
   * rotate lander) publishes its own closed-loop controller: the seat input
   * that flies `entity` (the player it moves) toward `goal` this tick —
   * arriving there at rest when `goal.press` is false. The solver flies such
   * a body only through it, one candidate per attractive affordance, and the
   * rollout still decides between them. */
  steer?(ctx: ModuleCtx<C>, entity: EntityV1, goal: SolverTargetV1): PlayerInputV1;
  /** A global WORLD module's solid ground profiles, for the platform to draw
   * (see {@link ComposedGroundV1}). */
  ground?(ctx: ModuleCtx<C>): ComposedGroundV1[];
}

/** Erased module type the registry and compiler hold. Configs are validated
 * against each module's own schema before any module code sees them. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyComposeModule = ComposeModuleV1<any>;

/** Identity helper that pins a module's config type. */
export function defineModule<C>(module: ComposeModuleV1<C>): ComposeModuleV1<C> {
  if (!/^[a-z]+\.[a-zA-Z][a-zA-Z0-9]*$/.test(module.id)) throw new Error(`invalid module id ${module.id}`);
  if (module.config.type !== 'object') throw new Error(`${module.id}: config schema must be an object`);
  const presented = Object.keys(module.presentation).sort().join(',');
  if (presented !== [...module.events].sort().join(',')) throw new Error(`${module.id}: presentation must cover exactly its events`);
  return Object.freeze(module);
}

/** A call into a GENERATED module's code that did not return: it ran past the
 * guest budget (`reason` names the bound, guestBudget.ts) or threw (its own
 * error, or a contract violation the runtime raised inside it). Thrown by the host that runs generated code
 * (generated/instantiate.ts), naming the module and the call, so a proof or
 * case run reports it as that module's inadmissible behaviour — never as the
 * host's own crash. */
export class ModuleCallError extends Error {
  constructor(readonly moduleId: string, readonly call: string, readonly reason: GuestFaultReasonV1, detail: string) {
    super(`${moduleId}'s ${call} call ${detail}`);
    this.name = 'ModuleCallError';
  }
}

/** The model-actionable failure of a run that a generated module call ended,
 * located where the run was (`rule.x's systems.rules call exceeded the 1 s
 * sandbox limit at tick 812`); any other error is the host's and rethrows. */
export function moduleCallFailure(error: unknown, where: string): string {
  if (error instanceof ModuleCallError) return `${error.message} at ${where}`;
  throw error;
}

/** Archetype names a module config references (validated to exist). */
export function archetypeRefsOf(module: AnyComposeModule, config: unknown): string[] {
  const refs: string[] = [];
  module.mapArchetypeRefs?.(config, name => { refs.push(name); return name; });
  return refs;
}

/** True when a config (any JSON value) holds one of `kinds` as a string
 * anywhere: the kinds a module could place are the ones its config names. */
export function configNamesKind(value: unknown, kinds: ReadonlySet<string>): boolean {
  if (typeof value === 'string') return kinds.has(value);
  if (Array.isArray(value)) return value.some(v => configNamesKind(v, kinds));
  return value !== null && typeof value === 'object' && Object.values(value).some(v => configNamesKind(v, kinds));
}

/** Read a number from JSON component data (modules own their shapes). */
export function num(data: JsonObject, key: string): number {
  const v = data[key];
  if (typeof v !== 'number') throw new Error(`component field ${key} is not a number`);
  return v;
}
export function str(data: JsonObject, key: string): string | null {
  const v: JsonValue | undefined = data[key];
  return typeof v === 'string' ? v : null;
}
export function bool(data: JsonObject, key: string): boolean {
  return data[key] === true;
}
